import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { STAGES, type Stage } from "../api";
import { useProjects } from "../App";
import { HealthMark, city } from "../components/ui";

const STAGE_WORDS: Record<Stage, string> = {
  build: "In build",
  demo: "Demo",
  pilot: "Pilot",
  released: "Released",
  "handed-off": "Handed off",
  retired: "Retired",
};

export function Projects() {
  const nav = useNavigate();
  const projects = useProjects();
  const [stage, setStage] = useState<Stage | "all">("all");
  const [q, setQ] = useState("");
  const [where, setWhere] = useState<"all" | "lab" | "outside">("all");
  const onLab = (p: { environments: { onLab: boolean }[] }) => p.environments.some((e) => e.onLab);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: projects.data?.length ?? 0 };
    for (const p of projects.data ?? []) c[p.stage] = (c[p.stage] ?? 0) + 1;
    return c;
  }, [projects.data]);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (projects.data ?? []).filter(
      (p) =>
        (stage === "all" || p.stage === stage) &&
        (where === "all" || (where === "lab") === onLab(p)) &&
        (!n || [p.name, p.slug, p.client, p.description].some((s) => s.toLowerCase().includes(n))),
    );
  }, [projects.data, stage, q, where]);
  const labCount = (projects.data ?? []).filter(onLab).length;

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Projects</h1>
          <p className="sub">Every client project the lab knows, where each copy runs and how it is doing.</p>
        </div>
        <Link className="btn primary" to="/new">New project</Link>
      </div>

      <div>
        <div className="tabs" role="tablist" aria-label="Stage">
          {(["all", ...STAGES] as const).filter((s) => s === "all" || counts[s]).map((s) => (
            <button key={s} role="tab" className="tab" aria-selected={stage === s} onClick={() => setStage(s)}>
              {s === "all" ? "All" : STAGE_WORDS[s]}<span className="n">{counts[s] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="toolbar" style={{ marginTop: 20 }}>
          <div className="actions">
            <input className="search-field" aria-label="Filter projects" placeholder="Filter by name, client or slug" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="input" style={{ height: 32, width: 190 }} aria-label="On the lab" value={where} onChange={(e) => setWhere(e.target.value as typeof where)}>
              <option value="all">On the lab or not</option>
              <option value="lab">On the lab ({labCount})</option>
              <option value="outside">Not on the lab ({(projects.data?.length ?? 0) - labCount})</option>
            </select>
          </div>
          <span className="stamp num">{rows.length} {rows.length === 1 ? "project" : "projects"}</span>
        </div>
      </div>

      <section className="panel flush" aria-label="Projects">
        {projects.error && <div className="item"><span className="error">Couldn't load projects: {projects.error.message}</span></div>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th style={{ width: "38%" }}>Project</th><th>Client</th><th>Stage</th><th>On the lab</th><th>Environments</th><th className="hide-phone">Where</th></tr>
            </thead>
            <tbody>
              {projects.isPending && [0, 1, 2, 3].map((i) => (
                <tr key={i}><td colSpan={6}><span className="skeleton" style={{ width: 260, height: 14 }} /></td></tr>
              ))}
              {rows.map((p) => (
                <tr key={p.id} className="link" onClick={() => nav(`/p/${p.slug}`)}>
                  <td>
                    <Link to={`/p/${p.slug}`} className="cell-main">{p.name}</Link>
                    <div className="cell-sub truncate" style={{ maxWidth: 440 }}>{p.description}</div>
                  </td>
                  <td>{p.client || <span className="muted">—</span>}</td>
                  <td><span className="pill">{STAGE_WORDS[p.stage]}</span></td>
                  <td>{onLab(p) ? <span className="pill positive">On the lab</span> : <span className="muted">Not yet</span>}</td>
                  <td>
                    <div style={{ display: "grid", gap: 4 }}>
                      {p.environments.length === 0 && <span className="muted">—</span>}
                      {p.environments.map((e) => (
                        <span key={e.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <span className="mono" style={{ minWidth: 56 }}>{e.name}</span>
                          <HealthMark health={e.health} reasons={e.healthReasons} />
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="hide-phone">{[...new Set(p.environments.map((e) => city(e.region)))].join(", ") || "—"}</td>
                </tr>
              ))}
              {projects.data && rows.length === 0 && (
                <tr><td colSpan={6}><div className="empty">No project matches “{q}”{stage !== "all" ? ` in ${STAGE_WORDS[stage].toLowerCase()}` : ""}.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
